import { html, css, LitElement } from 'lit-element';
import { a11yStyles } from './a11y.js';
import { CONTACT_EMAIL } from './utility.js';
import { t, tHtml, Localized, formatDate } from './i18n.js';

// Public data privacy page. Linked for every visitor, signed in or not, and
// reachable directly at /privacy (see intrivity_app.js's pathname check).
//
// The content below describes what the application actually does with data,
// derived from the code rather than from a template: Firebase Auth/Firestore/
// Storage, SurveyMonkey for the assessment, Stripe for payment, Gemini for AI
// summaries, Google Analytics and Cookiebot on the public pages.
//
// Items marked REVIEW are legal specifics that cannot be read off the code --
// retention periods, the identity of the data controller, and the contact
// route for requests. They must be filled in before this ships.
export class IntrivityPrivacy extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            :host {
                display: block;
                font-family: 'Muli', sans-serif;
                color: #222;
                max-width: 820px;
                margin: 0 auto;
                padding: 30px 20px 60px;
            }
            h1 {
                font-size: 1.8em;
                margin: 0 0 6px;
            }
            h2 {
                font-size: 1.15em;
                margin: 36px 0 10px;
                padding-bottom: 6px;
                border-bottom: 1px solid #e2e2e2;
            }
            .lede {
                color: #5c5c5c;
                max-width: 62ch;
                margin: 0 0 30px;
                line-height: 1.5;
            }
            p, li {
                line-height: 1.6;
                color: #444;
                max-width: 70ch;
            }
            ul {
                padding-left: 20px;
            }
            li {
                margin-bottom: 6px;
            }
            .caveat {
                border: 1px solid #e6d5a8;
                border-left: 5px solid rgb(166, 97, 0);
                border-radius: 6px;
                background: #fdf8ee;
                padding: 16px 18px;
            }
            .table-scroll {
                overflow-x: auto;
                max-width: 100%;
            }
            table {
                border-collapse: collapse;
                width: 100%;
                min-width: 520px;
                font-size: 0.92em;
                margin-top: 8px;
            }
            caption {
                text-align: left;
                padding: 4px 0 8px;
                color: #5c5c5c;
            }
            th, td {
                border: 1px solid #e2e2e2;
                padding: 9px 11px;
                text-align: left;
                vertical-align: top;
                color: #444;
            }
            thead th {
                background: #f7f7f7;
                color: #222;
                font-weight: 600;
            }
            tbody th {
                font-weight: 600;
                color: #222;
                white-space: nowrap;
            }
            a {
                color: rgb(23, 96, 102);
            }
            .meta {
                margin-top: 40px;
                padding-top: 14px;
                border-top: 1px solid #e2e2e2;
                font-size: 0.86em;
                color: #5c5c5c;
            }
        `];
    }

    render() {
        const services = ['firebase', 'surveymonkey', 'stripe', 'gemini', 'analytics', 'cookiebot', 'moodle'];
        return html`
            <h1>${t('privacy.title')}</h1>
            <p class="lede">${t('privacy.lede')}</p>

            <h2>${t('privacy.holdHeading')}</h2>
            <ul>
                <li>${tHtml('privacy.holdAccount')}</li>
                <li>${tHtml('privacy.holdProfile')}</li>
                <li>${tHtml('privacy.holdAnswers')}</li>
                <li>${tHtml('privacy.holdResults')}</li>
                <li>${tHtml('privacy.holdMembership')}</li>
            </ul>
            <p>${t('privacy.noCards')}</p>

            <h2>${t('privacy.whoHeading')}</h2>
            <p>${t('privacy.who1')}</p>
            <p>${t('privacy.who2')}</p>

            <h2>${t('privacy.thirdPartyHeading')}</h2>
            <p>${t('privacy.thirdPartyIntro')}</p>
            <div class="table-scroll">
                <table>
                    <caption>${t('privacy.tableCaption')}</caption>
                    <thead>
                        <tr>
                            <th scope="col">${t('privacy.colService')}</th>
                            <th scope="col">${t('privacy.colDoes')}</th>
                            <th scope="col">${t('privacy.colReceives')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${services.map((id) => html`
                            <tr>
                                <th scope="row">${t(`privacy.service.${id}.name`)}</th>
                                <td>${t(`privacy.service.${id}.does`)}</td>
                                <td>${t(`privacy.service.${id}.receives`)}</td>
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>

            <h2>${t('privacy.aiHeading')}</h2>
            <p>${t('privacy.ai1')}</p>
            <p>${t('privacy.ai2')}</p>

            <h2>${t('privacy.cookiesHeading')}</h2>
            <p>${t('privacy.cookies')}</p>

            <h2>${t('privacy.retentionHeading')}</h2>
            <p>${t('privacy.retention1')}</p>
            <p>${tHtml('privacy.retention2', { email: 'support@intrivity.com' })}</p>

            <h2>${t('privacy.choicesHeading')}</h2>
            <p>${tHtml('privacy.choices', { email: CONTACT_EMAIL })}</p>
            <div class="caveat">
                <p style="margin:0;">${tHtml('privacy.caveat')}</p>
            </div>

            <h2>${t('privacy.changesHeading')}</h2>
            <p>${t('privacy.changes')}</p>

            <p class="meta">${t('privacy.lastUpdated', { date: formatDate(new Date(2026, 7, 22), { dateStyle: 'long' }) })}</p>
        `;
    }
}

if (!customElements.get('intrivity-privacy')) customElements.define('intrivity-privacy', IntrivityPrivacy);
